import { ModalViewModel } from '@/infra/ModalViewModel'
import { required, validationContext } from 'validx'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'

/**
 * View model for the dialog that records an invoice paid outside the app.
 */
export class AddManualInvoiceDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Why the invoice is being added.
   */
  @observable
  accessor reason = ''

  /**
   * The invoice's Stripe ID.
   */
  @observable
  accessor stripeId = ''

  /**
   * The invoice's amount.
   */
  @observable
  accessor invoiceAmount = 0

  /**
   * Validation context for the form.
   */
  readonly validation = validationContext(this, {
    reason: [required('A reason is required')],
    stripeId: [required('Stripe Id is required')],
    invoiceAmount: [
      required('Invoice amount is required'),
      (opts) => opts.value !== 0 || 'Amount cannot be 0',
    ],
  })

  constructor(
    public readonly job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Validates the form.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Adds the invoice to the job.
   */
  add = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Adding Invoice', 'Hang tight!')
      .show()

    try {
      await this._jobStore.addManualInvoice({
        jobId: this.job.id,
        invoiceId: this.stripeId,
        amount: this.invoiceAmount,
        reason: this.reason,
        isDraft: this.job.status === 'UnderConstruction',
      })

      alert.withState('success').saying('Success!', 'Invoice has been added!')

      this.modal.close()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Adding the invoice failed')(err)
    }
  })

  /**
   * Clears the form.
   */
  @action.bound
  reset() {
    this.reason = ''
    this.stripeId = ''
    this.invoiceAmount = 0
    this.validation.reset()
  }
}
